import { PREPARING_STEP_ID } from '@/lib/assistant-cloud-local/step-ids';

export type ProgressStepStatus =
  | 'pending'
  | 'in-progress'
  | 'completed'
  | 'failed'
  // User aborted the step (e.g. declined an approval) — a neutral stop, not an error.
  | 'cancelled'
  // Waiting on the user's approve/decline decision — not active work, so no spinner.
  | 'awaiting-approval';

export interface AnyToolCallPart {
  type: 'tool-call';
  toolCallId: string;
  toolName: string;
  args: unknown;
  result?: unknown;
  isError?: boolean;
  /** AI-SDK v6 tool-part state (e.g. 'approval-requested', 'output-denied'). */
  state?: string;
  /** Server-side approval gate fields, when the tool call is approval-gated. */
  approval?: {
    approved?: boolean;
    reason?: string;
    resolution?: 'cancelled' | 'expired' | string;
  };
}

export type TrailResultStatus = 'results' | 'empty' | 'failed';

/**
 * Tool-tagging bucket. Drives only the **final** receipt copy — intermediate
 * gap / streaming rows are bucket-agnostic on purpose (the model's
 * mid-stream text isn't guaranteed to relate to the prior tool). The closer
 * uses the most-recent completed bucketed tool.
 */
export type TrailBucket =
  | 'cold'
  | 'postSearch'
  | 'postDates'
  | 'postInfo'
  | 'postKnowledge'
  | 'postRedirect'
  | 'postFilters';

export interface TrailStepRecord {
  id: string;
  kind: 'tool' | 'text';
  label: string;
  status: ProgressStepStatus;
  /** Bucket the tool contributes to. Set only for `kind: 'tool'` records whose tool config opts in. */
  toolBucket?: TrailBucket;
  /** Tool name from the original part — kept verbatim so done-label resolvers can aggregate by tool type. */
  toolName?: string;
  /**
   * Status of the call's result body — `results` (got items), `empty`
   * (succeeded but produced nothing), or `failed`. Drives status-aware
   * framing copy and the tenant done-label resolver. Falls back to the
   * record's overall `status` when no per-tool extractor is configured.
   */
  resultStatus?: TrailResultStatus;
  /**
   * Set on `kind: 'text'` records that have been opened but haven't emitted
   * any content yet (streaming = true, text = ""). The timeline builder
   * uses this flag to merge the preceding gap row INTO this row and show a
   * `preparing` (or `preparingLong`) variant until the first token arrives,
   * then morph back to the record's pre-computed `streaming` label.
   * Always false for `kind: 'tool'` records.
   */
  awaitsContent?: boolean;
}

export interface TimelineRow {
  id: string;
  label: string;
  status: ProgressStepStatus;
  kind: 'tool' | 'text' | 'thinking';
  durationMs: number;
  /** When set, the leaf reads `tickNow - liveAnchor` to display current elapsed; undefined for frozen rows. */
  liveAnchor?: number;
  /** Set on the row that should tick while running — at most one per timeline. */
  isInFlight?: boolean;
  /**
   * Stable React key for instance preservation across renders. When a tool
   * or text record absorbs a leading gap, its `morphKey` is set to the
   * thinking-row id that *would* have been emitted at that slot — so on the
   * next render the trailing thinking row and the new record share the same
   * React instance. The icon + text crossfades inside `TrailRow` then fire
   * for the morph (preparing → tool/text label) instead of the row being
   * unmounted and remounted with a hard cut. Falls back to `id` when not
   * morphing in.
   */
  morphKey?: string;
}

/** Once a gap has been open this long, the thinking row's label swaps from `preparing` to `longWait` (one-time). */
export const LONG_WAIT_AFTER_MS = 5000;
/** Initial "thinking..." row's max consumed duration before the rotating preparing row takes over the rest of the first gap. */
export const THINKING_BUDGET_MS = 3000;

const THINKING_GAP_PREFIX = `${PREPARING_STEP_ID}:gap:`;

/** Synthetic id for the always-on initial "thinking..." row that anchors the first position of the trail. */
export const ACTIVITY_TRAIL_THINKING_ID = `${THINKING_GAP_PREFIX}thinking`;

export function thinkingRowId(gapIndex: number): string {
  return `${THINKING_GAP_PREFIX}${gapIndex}`;
}

export function isThinkingRowId(id: string): boolean {
  return id.startsWith(THINKING_GAP_PREFIX);
}
